import type { InitOptions } from 'i18next';

export const LANGUAGES = {
  ENGLISH_BASE: 'en',
  ENGLISH_UK: 'en-GB',
  ENGLISH_AU: 'en-AU',
  SPANISH: 'es',
  GERMAN: 'de',
  FRENCH: 'fr',
  ITALIAN: 'it',
  JAPANESE: 'ja',
};
const SUPPORTED_LANGUAGE_CODES = Object.values(LANGUAGES);

export const DEFAULT_LANGUAGE = LANGUAGES.ENGLISH_BASE;

// Static imports to avoid webpack warning and bundle bloat
export const reactDatepickerLocaleImports: Record<string, () => Promise<{ default: Locale }>> = {
  [LANGUAGES.SPANISH]: () => import('date-fns/locale/es'),
  [LANGUAGES.GERMAN]: () => import('date-fns/locale/de'),
  [LANGUAGES.FRENCH]: () => import('date-fns/locale/fr'),
  [LANGUAGES.ITALIAN]: () => import('date-fns/locale/it'),
  [LANGUAGES.JAPANESE]: () => import('date-fns/locale/ja'),
};

export const getInitOptions = (overrides: Readonly<Partial<InitOptions>> = {}): InitOptions => ({
  // Default: 'all'
  // For now, we will only provide language and not region specific locale
  load: 'languageOnly',

  // Localization work will begin with charm (the guest site). We can avoid preloading
  // translations in prod for the rest of the app.
  // Default: false
  // preload: isDevelopment && ['en-US'],
  preload: [DEFAULT_LANGUAGE],
  // Default: 'dev'
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGE_CODES,

  returnEmptyString: false,
  // language will be lowercased EN --> en while leaving full locales like en-US
  cleanCode: true,
  // locale will be fully lowercased eg. en-US --> en-us

  interpolation: {
    escapeValue: false, // not needed for react as it escapes by default
  },

  // This option is necessary to tell i18next to try loading missing resources via
  // i18next-http-backend, otherwise no calls will be made if resources are defined.
  partialBundledLanguages: true,

  // i18next's LanguageDetector defaults to lookupQuerystring='lng', which collides
  // with the hotel/accommodations search 'lng' longitude URL param. Use 'l' (already
  // recognized in LANGUAGE_PARAMS) instead. See JW-47.
  // Only `lookupQuerystring` is overridden — `order` and other detection options
  // fall through to plugin defaults (server vs browser differ), avoiding an
  // unintended narrowing of server-side detection (e.g. dropping `path`/`session`).
  detection: {
    lookupQuerystring: 'l',
  },

  react: {
    useSuspense: true,
    wait: !process?.release,
  },
  postProcess: [`reactPostprocessor`],
  ...overrides,
});

type ParsedFormat = {
  dateFormat: string[];
  dateInputMask: string[];
};

export const getDateFormatString = (locale: string) => {
  const formatObj = new Intl.DateTimeFormat(locale).formatToParts(new Date());
  const parsed = formatObj.reduce(
    (acc, obj) => {
      switch (obj.type) {
        case 'day':
          acc.dateFormat.push('dd');
          acc.dateInputMask.push('99');
          break;
        case 'month':
          acc.dateFormat.push('MM');
          acc.dateInputMask.push('99');
          break;
        case 'year':
          acc.dateFormat.push('yyyy');
          acc.dateInputMask.push('9999');
          break;
      }
      return acc;
    },
    { dateFormat: [], dateInputMask: [] } as ParsedFormat,
  );
  return {
    dateFormat: parsed.dateFormat.join('/'),
    dateInputMask: parsed.dateInputMask.join('/'),
  };
};

export const languageSupported = (extendedLanguageCode: string): boolean => {
  const [rawLanguage, rawRegion] = extendedLanguageCode.split('-');
  const [languageCode, regionCode] = [rawLanguage?.toLowerCase(), rawRegion?.toUpperCase()];

  const fullMatch = regionCode ? SUPPORTED_LANGUAGE_CODES.includes(`${languageCode}-${regionCode}`) : false;
  const languageMatch = languageCode ? SUPPORTED_LANGUAGE_CODES.includes(languageCode) : false;

  return fullMatch || languageMatch;
};
